import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { formatDateTime, getErrorMessage } from "./reportShared";

const EMPTY_EDIT = {
    id: "",
    name: "",
    message_type: "",
    subject: "",
    message: "",
    active: true,
    description: "",
};

export default function OutgoingMessageTemplatesReport({
                                                           apiUrl,
                                                           getAccessToken,
                                                           keycloak,
                                                       }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [messageTypeFilter, setMessageTypeFilter] = useState("");
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [edit, setEdit] = useState(EMPTY_EDIT);

    const messageTypes = useMemo(
        () =>
            Array.from(
                new Set(
                    templates
                        .map((template) => String(template?.message_type ?? "").trim())
                        .filter(Boolean)
                )
            ).sort((a, b) => a.localeCompare(b)),
        [templates]
    );

    const loadTemplates = useCallback(async () => {
        if (!apiUrl) {
            setError("REACT_APP_API_URL is not configured.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const token = await getAccessToken();
            const params = new URLSearchParams();

            if (activeFilter !== "all") {
                params.set("active", activeFilter);
            }

            if (messageTypeFilter.trim()) {
                params.set("message_type", messageTypeFilter.trim());
            }

            const query = params.toString();
            const response = await fetch(
                `${apiUrl}/consent/outgoing-message-templates${query ? `?${query}` : ""}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const responseBody = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(
                    responseBody?.details ||
                    responseBody?.error ||
                    responseBody?.message ||
                    `Unable to load message templates. HTTP ${response.status}.`
                );
            }

            setTemplates(
                Array.isArray(responseBody?.templates)
                    ? responseBody.templates
                    : Array.isArray(responseBody)
                        ? responseBody
                        : []
            );
        } catch (loadError) {
            setError(
                getErrorMessage(loadError, "Unable to load outgoing message templates.")
            );
        } finally {
            setLoading(false);
        }
    }, [activeFilter, apiUrl, getAccessToken, messageTypeFilter]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const openEdit = (template) => {
        setEditingTemplate(template);
        setEdit({
            id: template?.id || "",
            name: template?.name || "",
            message_type: template?.message_type || "",
            subject: template?.subject || "",
            message: template?.message || "",
            active: Boolean(template?.active),
            description: template?.description || "",
        });
        setError("");
    };

    const closeEdit = () => {
        if (saving) return;
        setEditingTemplate(null);
        setEdit(EMPTY_EDIT);
    };

    const saveTemplate = async () => {
        if (!edit.id) return;

        if (!String(edit.message).trim()) {
            setError("Message is required.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = await getAccessToken();
            const updatedBy = String(keycloak?.tokenParsed?.email ?? "").trim();

            const response = await fetch(
                `${apiUrl}/consent/outgoing-message-templates/${encodeURIComponent(edit.id)}`,
                {
                    method: "PUT",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        subject: String(edit.subject).trim() || null,
                        message: edit.message,
                        active: Boolean(edit.active),
                        description: String(edit.description).trim() || null,
                        updated_by: updatedBy || null,
                    }),
                }
            );

            const responseBody = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(
                    responseBody?.details ||
                    responseBody?.error ||
                    responseBody?.message ||
                    `Unable to update message template. HTTP ${response.status}.`
                );
            }

            closeEdit();
            await loadTemplates();
        } catch (saveError) {
            setError(
                getErrorMessage(saveError, "Unable to update the message template.")
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "220px 280px auto",
                        },
                        alignItems: "center",
                    }}
                >
                    <FormControl fullWidth size="small">
                        <InputLabel id="template-active-filter-label">Status</InputLabel>
                        <Select
                            labelId="template-active-filter-label"
                            label="Status"
                            value={activeFilter}
                            onChange={(event) => setActiveFilter(event.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="template-type-filter-label">Message Type</InputLabel>
                        <Select
                            labelId="template-type-filter-label"
                            label="Message Type"
                            value={messageTypeFilter}
                            onChange={(event) => setMessageTypeFilter(event.target.value)}
                        >
                            <MenuItem value="">All Message Types</MenuItem>
                            {messageTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Button
                            variant="outlined"
                            onClick={loadTemplates}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} /> : null}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && <Alert severity="error">{error}</Alert>}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle1">Outgoing Message Templates</Typography>
                            <Typography variant="body2" color="text.secondary">
                                View and update the templates used for outgoing messages.
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {templates.length.toLocaleString()} shown
                        </Typography>
                    </Box>

                    <TableContainer sx={{ maxHeight: "58vh" }}>
                        <Table stickyHeader size="small" aria-label="Outgoing message templates">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Message Type</TableCell>
                                    <TableCell>Subject</TableCell>
                                    <TableCell>Active</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell>Updated By</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                            <CircularProgress size={28} />
                                        </TableCell>
                                    </TableRow>
                                ) : templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            No outgoing message templates were found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    templates.map((template) => (
                                        <TableRow key={template.id} hover>
                                            <TableCell>{template.name || ""}</TableCell>
                                            <TableCell>{template.message_type}</TableCell>
                                            <TableCell>{template.subject || ""}</TableCell>
                                            <TableCell>{template.active ? "Yes" : "No"}</TableCell>
                                            <TableCell>{formatDateTime(template.updated_on)}</TableCell>
                                            <TableCell>{template.updated_by || ""}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => openEdit(template)}>
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Paper>

            <Dialog open={Boolean(editingTemplate)} onClose={closeEdit} fullWidth maxWidth="md">
                <DialogTitle>Edit Outgoing Message Template</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Box
                            sx={{
                                display: "grid",
                                gap: 2,
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            }}
                        >
                            <TextField
                                label="Name"
                                value={edit.name}
                                disabled
                                helperText="Template name cannot be changed."
                            />

                            <TextField
                                label="Message Type"
                                value={edit.message_type}
                                disabled
                                helperText="Message type cannot be changed."
                            />
                        </Box>

                        <TextField
                            label="Subject"
                            value={edit.subject}
                            onChange={(event) =>
                                setEdit((current) => ({ ...current, subject: event.target.value }))
                            }
                        />
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                bgcolor: "grey.50",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 0.5 }}
                            >
                                Available Variables
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                            >
                                These variables are replaced automatically when the message is sent.
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "220px 1fr",
                                    },
                                    columnGap: 2,
                                    rowGap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    component="code"
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {"{{facility_name}}"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Facility Name
                                </Typography>

                                <Typography
                                    component="code"
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {"{{provider_number}}"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Provider Number
                                </Typography>

                                <Typography
                                    component="code"
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {"{{consent_link}}"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Consent Form Link
                                </Typography>
                            </Box>
                        </Paper>


                        <Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    mb: 1,
                                }}
                            >
                                <Typography variant="subtitle2">
                                    Message
                                </Typography>

                                {String(edit.message_type ?? "")
                                    .trim()
                                    .toLowerCase() === "email" && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setPreviewOpen(true)}
                                    >
                                        Preview Email
                                    </Button>
                                )}
                            </Box>

                            <TextField
                                required
                                fullWidth
                                multiline
                                minRows={12}
                                label={
                                    String(edit.message_type ?? "")
                                        .trim()
                                        .toLowerCase() === "email"
                                        ? "Message HTML"
                                        : "Message"
                                }
                                value={edit.message}
                                onChange={(event) =>
                                    setEdit((current) => ({
                                        ...current,
                                        message: event.target.value,
                                    }))
                                }
                                helperText={
                                    String(edit.message_type ?? "")
                                        .trim()
                                        .toLowerCase() === "email"
                                        ? "Email templates are stored as HTML."
                                        : ""
                                }
                            />
                        </Box>

                        <TextField
                            multiline
                            minRows={3}
                            label="Description"
                            value={edit.description}
                            onChange={(event) =>
                                setEdit((current) => ({
                                    ...current,
                                    description: event.target.value,
                                }))
                            }
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={edit.active}
                                    onChange={(event) =>
                                        setEdit((current) => ({
                                            ...current,
                                            active: event.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Active"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveTemplate}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} /> : null}
                    >
                        {saving ? "Saving" : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Email Preview</DialogTitle>
                <DialogContent dividers>
                    {edit.subject && (
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Subject
                            </Typography>
                            <Typography variant="subtitle1">
                                {edit.subject}
                            </Typography>
                        </Box>
                    )}

                    <Box
                        component="iframe"
                        title="Email template preview"
                        srcDoc={edit.message || ""}
                        sx={{
                            width: "100%",
                            minHeight: "65vh",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: "common.white",
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>
                        Close Preview
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
